import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Copy,
  Pause,
  Pencil,
  Play,
  Send,
} from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  ImChannelProactivityExperiment,
  ImChannelProactivityExperimentKind,
  ProactivenessFilter,
  Weekday,
} from '@/features/im-channel-proactivity-experiments/im-channel-proactivity-experiments-types';
import {
  WEEKDAY_INDEX,
  WEEKDAY_LABELS,
} from '@/features/im-channel-proactivity-experiments/weekdays';

type ExperimentsTableProps = {
  experiments: ImChannelProactivityExperiment[];
  section?: ProactivenessFilter;
  isLoading: boolean;
  error: Error | null;
  sendingSlug?: string;
  changingPauseSlug?: string;
  onEdit: (experiment: ImChannelProactivityExperiment) => void;
  onDuplicate: (experiment: ImChannelProactivityExperiment) => void;
  onSend: (experiment: ImChannelProactivityExperiment) => void;
  onPauseChange: (
    experiment: ImChannelProactivityExperiment,
    isPaused: boolean
  ) => void;
};

type ExperimentTableItemProps = {
  experiment: ImChannelProactivityExperiment;
  now: Date;
  isSending: boolean;
  isChangingPause: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onSend: () => void;
  onPauseChange: (isPaused: boolean) => void;
};

const KIND_LABELS: Record<ImChannelProactivityExperimentKind, string> = {
  watcher: 'Watcher',
  digest: 'Digest',
};

const MINUTE_MS = 60_000;
const MINUTES_PER_HOUR = 60;

const runsOnDay = (weekdays: Weekday[], date: Date) =>
  weekdays.length === 0 ||
  weekdays.some((day) => WEEKDAY_INDEX[day] === date.getDay());

const nextTriggerDelta = (
  scheduleTimes: string[],
  weekdays: Weekday[],
  now: Date
) => {
  const nextTriggerAt = Math.min(
    ...scheduleTimes.map((scheduleTime) => {
      const [hour, minute] = scheduleTime.split(':').map(Number);
      const triggerAt = new Date(now);
      triggerAt.setHours(hour, minute, 0, 0);
      while (triggerAt <= now || !runsOnDay(weekdays, triggerAt)) {
        triggerAt.setDate(triggerAt.getDate() + 1);
      }
      return triggerAt.getTime();
    })
  );
  const totalMinutes = Math.max(
    1,
    Math.ceil((nextTriggerAt - now.getTime()) / MINUTE_MS)
  );
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;

  return [hours && `${hours}h`, minutes && `${minutes}m`]
    .filter(Boolean)
    .join(' ');
};

const scheduleLabel = (weekdays: Weekday[]) =>
  weekdays.length === 0
    ? 'Daily'
    : weekdays.map((day) => WEEKDAY_LABELS[day]).join(', ');

const assignmentStatus = (experiment: ImChannelProactivityExperiment) => {
  if (experiment.assigned_user_count === 0) return 'No assigned users';
  if (experiment.admin_paused_user_count > 0) {
    return `${experiment.active_user_count}/${experiment.assigned_user_count} active · ${experiment.admin_paused_user_count} admin-paused`;
  }
  if (experiment.active_user_count === 0) {
    return 'All copies individually paused';
  }
  return `${experiment.active_user_count}/${experiment.assigned_user_count} active`;
};

const ExperimentTableItem = ({
  experiment,
  now,
  isSending,
  isChangingPause,
  onEdit,
  onDuplicate,
  onSend,
  onPauseChange,
}: ExperimentTableItemProps) => {
  const hasAdminPausedAssignments = experiment.admin_paused_user_count > 0;
  const hasActiveAssignments = experiment.active_user_count > 0;

  return (
    <Fragment>
      <TableRow>
        <TableCell className="max-w-xl align-top">
          <p className="font-medium">{experiment.title}</p>
          <p className="mt-1 text-xs font-medium text-muted-foreground">
            {experiment.section === 'production' ? 'Production' : 'Experiment'}
          </p>
          <p className="mt-1 font-mono text-xs text-muted-foreground">
            {experiment.slug}
          </p>
          {experiment.description && (
            <p className="mt-2 text-muted-foreground">
              {experiment.description}
            </p>
          )}
        </TableCell>
        <TableCell className="align-top text-xs text-muted-foreground">
          <p className="font-mono">{experiment.rollout_slug}</p>
          {experiment.flag_key !== experiment.rollout_slug && (
            <p className="mt-1">Flag: {experiment.flag_key}</p>
          )}
        </TableCell>
        <TableCell className="align-top text-xs text-muted-foreground">
          {experiment.required_integrations.join(', ') || 'None'}
        </TableCell>
        <TableCell className="align-top">
          <p>
            {scheduleLabel(experiment.weekdays)} at{' '}
            {experiment.schedule_times.join(', ')}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Next locally in{' '}
            {nextTriggerDelta(
              experiment.schedule_times,
              experiment.weekdays,
              now
            )}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {KIND_LABELS[experiment.kind]}
          </p>
        </TableCell>
        <TableCell className="align-top">
          {assignmentStatus(experiment)}
        </TableCell>
        <TableCell className="align-top">
          <section className="flex flex-wrap justify-end gap-2">
            {(hasAdminPausedAssignments || hasActiveAssignments) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPauseChange(!hasAdminPausedAssignments)}
                disabled={isChangingPause}
              >
                {hasAdminPausedAssignments ? <Play /> : <Pause />}
                {isChangingPause
                  ? 'Updating...'
                  : hasAdminPausedAssignments
                    ? 'Resume admin-paused'
                    : 'Pause active'}
              </Button>
            )}
            {experiment.section === 'experimental' && hasActiveAssignments && (
              <Button
                variant="outline"
                size="sm"
                onClick={onSend}
                disabled={isSending}
              >
                <Send />
                {isSending ? 'Queuing...' : 'Send now'}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil />
              Edit
            </Button>
            <Button variant="outline" size="sm" onClick={onDuplicate}>
              <Copy />
              Duplicate
            </Button>
          </section>
        </TableCell>
      </TableRow>
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={6} className="p-0">
          <details className="group">
            <summary className="flex cursor-pointer list-none items-center gap-2 px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden">
              <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
              View prompt
            </summary>
            <p className="whitespace-pre-wrap bg-muted px-6 py-5 text-base leading-7 text-foreground">
              {experiment.prompt}
            </p>
          </details>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

/** Renders experiment definitions and delegates their actions to the page. */
export const ExperimentsTable = ({
  experiments,
  section = 'all',
  isLoading,
  error,
  sendingSlug,
  changingPauseSlug,
  onEdit,
  onDuplicate,
  onSend,
  onPauseChange,
}: ExperimentsTableProps) => {
  const [now, setNow] = useState(() => new Date());
  const [rolloutSortAscending, setRolloutSortAscending] = useState(true);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), MINUTE_MS);
    return () => window.clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <p className="py-12 text-center text-muted-foreground">
        Loading proactive routines...
      </p>
    );
  }
  if (error) {
    return (
      <p className="rounded-md border border-destructive px-4 py-3 text-sm text-destructive">
        {error.message}
      </p>
    );
  }
  if (experiments.length === 0) {
    return (
      <p className="py-12 text-center text-muted-foreground">
        {section === 'production'
          ? 'No production proactive routines configured.'
          : section === 'experimental'
            ? 'No internal experiments configured.'
            : 'No proactive routines configured.'}
      </p>
    );
  }

  const sortedExperiments = [...experiments].sort(
    (left, right) =>
      left.rollout_slug.localeCompare(right.rollout_slug) *
      (rolloutSortAscending ? 1 : -1)
  );

  return (
    <Table containerClassName="rounded-md border">
      <TableHeader>
        <TableRow>
          <TableHead>
            {section === 'experimental' ? 'Experiment' : 'Routine'}
          </TableHead>
          <TableHead
            aria-sort={rolloutSortAscending ? 'ascending' : 'descending'}
          >
            <button
              type="button"
              className="flex items-center gap-1 hover:text-foreground"
              onClick={() => setRolloutSortAscending((ascending) => !ascending)}
            >
              Rollout
              {rolloutSortAscending ? (
                <ArrowUp className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <ArrowDown className="h-3.5 w-3.5" aria-hidden />
              )}
            </button>
          </TableHead>
          <TableHead>Eligible integrations</TableHead>
          <TableHead>Schedule</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedExperiments.map((experiment) => (
          <ExperimentTableItem
            key={experiment.slug}
            experiment={experiment}
            now={now}
            isSending={sendingSlug === experiment.slug}
            isChangingPause={changingPauseSlug === experiment.slug}
            onEdit={() => onEdit(experiment)}
            onDuplicate={() => onDuplicate(experiment)}
            onSend={() => onSend(experiment)}
            onPauseChange={(isPaused) => onPauseChange(experiment, isPaused)}
          />
        ))}
      </TableBody>
    </Table>
  );
};
